import type { Contact } from '@conversia/shared-types';
import { formatPhoneNumber } from './format';

/**
 * Helpers unificados para mostrar la identidad de un contacto en TODA la UI.
 *
 * Problema (v0.4): dos contactos distintos pueden tener el mismo pushName de
 * WhatsApp ("Esteban Silva") y se ven idénticos. La solución es mostrar siempre
 * el nombre acompañado de un subtítulo que los diferencie (el número, o los
 * últimos dígitos del JID si no hay número).
 */

/** Campos mínimos necesarios para resolver nombre/subtítulo de un contacto. */
export type ContactLike = Pick<Contact, 'externalId'> &
  Partial<Pick<Contact, 'name' | 'pushName' | 'phone' | 'isGroup'>>;

function isLidExternalId(externalId?: string | null): boolean {
  return !!externalId?.endsWith('@lid');
}

/**
 * Nombre a mostrar. Prioridad: name editado a mano > pushName de WhatsApp >
 * teléfono > etiqueta LID > externalId. Para grupos sin nombre cae a "Grupo".
 */
export function getContactDisplayName(contact?: ContactLike | null): string {
  if (!contact) return 'Contacto';
  return (
    contact.name?.trim() ||
    contact.pushName?.trim() ||
    contact.phone?.trim() ||
    (isLidExternalId(contact.externalId) ? 'Contacto LID' : null) ||
    contact.externalId ||
    (contact.isGroup ? 'Grupo' : 'Contacto')
  );
}

/**
 * Subtítulo que diferencia contactos con nombre parecido. Devuelve el número
 * formateado si existe; para `@lid` sin teléfono, un sufijo corto; si no, los
 * últimos caracteres del externalId.
 */
export function getContactSubtitle(contact?: ContactLike | null): string {
  if (!contact) return '';
  if (contact.phone?.trim()) return formatPhoneNumber(contact.phone);
  const externalId = contact.externalId;
  if (!externalId) return '';
  if (isLidExternalId(externalId)) {
    const suffix = externalId.replace(/@lid$/, '').slice(-4);
    return suffix ? `LID · …${suffix}` : 'LID';
  }
  return externalId.slice(-8);
}
